import {
  NavigationData,
  NavigationCheckpointData,
  NavigationDataResponse,
} from "../types/navigation";
import {
  DEFAULT_NAV_MODE,
  NavigationMode,
  SUPPORTED_MODE,
} from "../types/navigation-mode";
import { create } from "zustand";
import { persist } from "zustand/middleware";

import * as pc from "playcanvas";
import { tts } from "@/api/polly";
import { isIOSMobileBrowser } from "@/utils/device";

type AutoPlayData = {
  time: number;
  delay: number;
};

export type ChatHistory = {
  from: string;
  message: string;
};

export interface NavigationState {
  // conversationId: string;
  // setConversationId: (conversationId: string) => void;
  chatHistory: ChatHistory[];
  appendChatHistory: (chatHistory: ChatHistory) => void;
  clearChatHistory: () => void;

  useCurrentLocation: boolean;
  setUseCurrentLocation: (value: boolean) => void;

  navFrom: string;
  setNavFrom: (from: string) => void;

  traceId: string;
  setTraceId: (traceId: string) => void;

  chatAnswer: string;
  setChatAnswer: (answer: string) => void;

  navMode: string;
  currentMode: string;
  setCurrentMode: (mode: string) => void;
  setNavMode: (mode: string) => void;
  getNavModeName: (mode: string) => string;

  // useStairs: boolean;
  // setUseStairs: (mode: boolean) => void;

  userQuery: string;
  setUserQuery: (query: string) => void;

  progress: number;
  setProgress: (progress: number) => void;
  calculatePosition: (percentage: number, mode: number) => void;
  setCalculatePosition: (
    fn: (percentage: number, mode: number) => void,
  ) => void;

  navData?: NavigationDataResponse;
  setNavData: (navData: NavigationDataResponse, language: string) => void;
  fullDistance: number;
  setFullDistance: (distance: number) => void;
  remainingDistance: number;
  setRemainingDistance: (distance: number) => void;

  fullDuration: number;
  setFullDuration: (distance: number) => void;
  remainingDuration: number;
  setRemainingDuration: (distance: number) => void;

  currentVenueId: string;
  setCurrentVenueId: (venueId: string) => void;

  currentVenue: string;
  setCurrentVenue: (venue: string) => void;

  currentLevel: string;
  setCurrentLevel: (level: string) => void;

  currentLevelIndex: number;
  setCurrentLevelIndex: (level: number) => void;

  startPointName: string;
  setStartPointName: (venue: string) => void;

  endPointName: string;
  setEndPointName: (venue: string) => void;

  navigationMessage: string;
  setNavigationMessage: (level: string) => void;

  navigationAngle: number;
  setNavigationAngle: (angle: number) => void;

  startAutoPlayData: AutoPlayData;
  startAutoPlayDelay: (delay: number) => void;
  resetStartAutoPlayDelay: () => void;

  stopAutoPlayData: number;
  stopAutoPlay: () => void;

  // instantUpdateEvent: number;
  // instantUpdate: () => void;
  // completeInstantUpdate: () => void;

  cameraMode: number;
  setCameraMode: (mode: number) => void;
  switchCameraMode: () => void;

  //0 = no transparent version , 1 = disable , 2 = enable
  xRayMode: number;
  enableXRayMode: () => void;
  setXRayMode: (mode: number) => void;
  switchXRayMode: () => void;

  isShowTopViewNavigation: boolean;
  setIsShowTopViewNavigation: (isShow: boolean) => void;

  navigationCheckpoint: number[];
  setNavigationCheckpoint: (checkpoints: number[]) => void;

  navigationPositionArray: pc.Vec3[];
  setNavigationPositionArray: (data: pc.Vec3[]) => void;

  navigationCameraPosition: pc.Vec3;
  setNavigationCameraPosition: (data: pc.Vec3) => void;

  navigationCameraLookingPosition: pc.Vec3;
  setNavigationCameraLookingPosition: (data: pc.Vec3) => void;

  venueNotSupported: boolean;
  setVenueNotSupported: (value: boolean) => void;
  apiError: string;
  setApiError: (value: string) => void;

  navigationCheckpointDataArray: NavigationCheckpointData[];
  setNavigationCheckpointDataArray: (value: NavigationCheckpointData[]) => void;

  navigationPercentagePositionDictionary: Record<number, number[]>;
  setNavigationPercentagePositionDictionary: (
    data: Record<number, number[]>,
  ) => void;

  isLoading: boolean;
  setLoading: (value: boolean) => void;

  isInterrupt: boolean;
  setInterrupt: (value: boolean) => void;

  showNavAIPanel: boolean;
  setShowNavAIPanel: (value: boolean) => void;

  isShowNavigationContinueButton: boolean;
  setIsShowNavigationContinueButton: (value: boolean) => void;
  isShowNavigationRestartButton: boolean;
  setIsShowNavigationRestartButton: (value: boolean) => void;

  startVenueId: string;
  startMapId: string;
  startCoordinate: string;
  startPoi: string;
  setStartPosition: (
    venueId: string,
    mapId: string,
    position: string,
    poi: string,
  ) => void;

  resetNavigationSessionData: () => void;

  isDisplayUI: boolean;
  setIsDisplayUI: (value: boolean) => void;

  audioMap: Map<string, string>;
  setAudioMap: (audioMap: Map<string, string>) => void;

  isDisplayDragScreenSliderAnimation: boolean;
  setIsDisplayDragScreenSliderAnimation: (value: boolean) => void;

}

async function preloadAudio(textList: string[], language: string) {

  const audioMap = new Map<string, string>();

  const results = await Promise.all(textList.map(text => tts(text, language)));

  results.forEach(({ text, audioUrl }) => {
    audioMap.set(text, audioUrl);
  });

  return audioMap
}

export const useNavigationStore = create<NavigationState>()(
  persist(
    (set, get) => ({
      navMode: DEFAULT_NAV_MODE, // navigation mode in Settings (will not be affected when user changed nav mode in slider)
      // useStairs: DEFAULT_USE_STAIRS,
      currentMode: DEFAULT_NAV_MODE, // nav mode in slider and mode selector

      userQuery: "",

      // conversationId: "",
      chatHistory: [],
      appendChatHistory: (chat: ChatHistory) =>
        set((state) => ({
          chatHistory: [...state.chatHistory, chat], // Immutable append
        })),
      clearChatHistory: () => set({ chatHistory: [] }),

      useCurrentLocation: false,
      setUseCurrentLocation: (value: boolean) =>
        set({ useCurrentLocation: value }),

      navFrom: "",
      setNavFrom: (value: string) => set({ navFrom: value }),

      chatAnswer: "",
      setChatAnswer: (answer: string) => {
        let shorterAnswer = answer
          .replace(
            /The navigation route is now ready for you to follow in the app\.? ?/i,
            "",
          )
          .trim();
        if (shorterAnswer.includes("##")) {
          shorterAnswer = shorterAnswer.substring(
            0,
            shorterAnswer.indexOf("###"),
          );
          set({ chatAnswer: shorterAnswer });
        }
      },
      venueNotSupported: false,
      apiError: "",

      // setConversationId: (conversationId: string) =>
      //   set({ conversationId: conversationId }),

      traceId: "",
      setTraceId: (traceId: string) => set({ traceId: traceId }),

      setNavMode: (mode: string) => set({ navMode: mode, currentMode: mode }),

      getNavModeName: (mode: string) => {
        const navMode = SUPPORTED_MODE.find(
          (nav: NavigationMode) => nav.code === mode,
        );
        return navMode?.name || mode.toUpperCase();
      },

      setCurrentMode: (mode: string) => set({ currentMode: mode }),

      // setUseStairs: (useStairs: boolean) => set({useStairs: useStairs}),

      setUserQuery: (query: string) => set({ userQuery: query }),

      progress: 0,

      setProgress: (progress: number) => {
        set({ progress: progress });
        const fullDistance = get().fullDistance;
        if (fullDistance !== -1) {
          get().setRemainingDistance(fullDistance - fullDistance * progress);
        }
        const fullDuration = get().fullDuration;
        if (fullDuration !== -1) {
          get().setRemainingDuration(fullDuration - fullDuration * progress);
        }
      },
      // set api response data
      audioMap: new Map<string, string>(),
      setAudioMap: (audioMap: Map<string, string>) => {
        set({
          audioMap: audioMap,
        });
      },
      setNavData: async (navData, language) => {
        // console.log("set navData !!!!!");
        // console.log(navData);
        // localStorage.setItem('navData', JSON.stringify(navData));
        let shorterAnswer = navData.message;
        if (navData.message && navData.message.includes("##")) {
          shorterAnswer = shorterAnswer.substring(
            0,
            shorterAnswer.indexOf("###"),
          );
        }
        let audioMap = new Map<string, string>()

        console.log("isIOSMobileBrowser() ", isIOSMobileBrowser())
        if (!isIOSMobileBrowser()) {
          let ttsList: string[] = ["Start a step-by-step walkthrough.", "開始一步步講解", shorterAnswer]
          navData.nav[0].navigation.map(nav => {
            if (nav.message) ttsList.push(nav.message)
          })
          audioMap = await preloadAudio(ttsList, language)
        }
        set({
          navData: navData,
          audioMap: audioMap,
          // conversationId: navData.conversation_id,
          chatAnswer: shorterAnswer,
        });
      },

      // Placeholder function
      calculatePosition: () => { },
      setCalculatePosition: (fn) => set({ calculatePosition: fn }),

      fullDistance: -1,
      setFullDistance: (distance: number) => {
        set({ fullDistance: distance });
        get().setRemainingDistance(distance);
      },
      remainingDistance: -1,
      setRemainingDistance: (distance: number) => {
        set({ remainingDistance: distance });
      },
      fullDuration: -1,
      setFullDuration: (duration: number) => {
        set({ fullDuration: duration });
        get().setRemainingDuration(duration);
      },
      remainingDuration: -1,
      setRemainingDuration: (duration: number) => {
        set({ remainingDuration: duration });
      },

      currentVenueId: "",
      setCurrentVenueId: (venueId: string) => {
        set({ currentVenueId: venueId });
      },

      currentVenue: "",
      setCurrentVenue: (venue: string) => {
        set({ currentVenue: venue });
      },

      currentLevel: "",
      setCurrentLevel: (level: string) => {
        set({ currentLevel: level });
      },

      currentLevelIndex: 0,
      setCurrentLevelIndex: (level: number) => {
        set({ currentLevelIndex: level });
      },

      startPointName: "",
      setStartPointName: (name: string) => {
        set({ startPointName: name });
      },

      endPointName: "",
      setEndPointName: (name: string) => {
        set({ endPointName: name });
      },

      navigationMessage: "",
      setNavigationMessage: (message: string) => {
        set({ navigationMessage: message });
      },

      navigationAngle: 0,
      setNavigationAngle: (angle: number) => {
        set({ navigationAngle: angle });
      },

      startAutoPlayData: { time: -1, delay: -1 },
      startAutoPlayDelay: (delay: number) => {
        set({ startAutoPlayData: { time: Date.now(), delay: delay } });
      },
      resetStartAutoPlayDelay: () => {
        set({ startAutoPlayData: { time: -1, delay: -1 } });
      },

      stopAutoPlayData: -1,
      stopAutoPlay: () => {
        set({ stopAutoPlayData: Date.now() });
      },

      // instantUpdateEvent: -1,
      // instantUpdate: () => {
      //   set({ instantUpdateEvent: Date.now() });
      // },
      // completeInstantUpdate: () => {
      //   set({ instantUpdateEvent: -1 });
      // },

      cameraMode: 0,
      setCameraMode: (mode: number) => {
        set({ cameraMode: mode });
      },
      switchCameraMode: () => {
        set((state) => ({ cameraMode: state.cameraMode === 0 ? 1 : 0 }));
      },

      xRayMode: 0,
      enableXRayMode: () => {
        set((state) => ({
          xRayMode: state.xRayMode === 0 ? 1 : state.xRayMode,
        }));
      },
      setXRayMode: (mode: number) => {
        set((state) => ({ xRayMode: mode }));
      },
      switchXRayMode: () => {
        set((state) => ({ xRayMode: state.xRayMode === 1 ? 2 : 1 }));
      },

      isShowTopViewNavigation: true,
      setIsShowTopViewNavigation: (isShow: boolean) => {
        set({ isShowTopViewNavigation: isShow });
      },

      navigationPositionArray: [],
      setNavigationPositionArray: (data: pc.Vec3[]) => {
        set({ navigationPositionArray: data });
      },

      navigationCameraPosition: new pc.Vec3(0, 0, 0),
      setNavigationCameraPosition: (data: pc.Vec3) => {
        set({ navigationCameraPosition: data });
      },

      navigationCameraLookingPosition: new pc.Vec3(0, 0, 0),
      setNavigationCameraLookingPosition: (data: pc.Vec3) => {
        set({ navigationCameraPosition: data });
      },

      setVenueNotSupported: (value: boolean) => {
        set({ venueNotSupported: value });
      },
      setApiError: (value: string) => {
        set({ apiError: value });
      },

      navigationCheckpoint: [],
      setNavigationCheckpoint: (checkpoints: number[]) => {
        set({ navigationCheckpoint: checkpoints });
      },

      navigationCheckpointDataArray: [],
      setNavigationCheckpointDataArray: (array: NavigationCheckpointData[]) => {
        set({ navigationCheckpointDataArray: array });
      },

      navigationPercentagePositionDictionary: {},
      setNavigationPercentagePositionDictionary: (
        data: Record<number, number[]>,
      ) => {
        set({ navigationPercentagePositionDictionary: data });
      },

      isLoading: false,
      setLoading: (value: boolean) => {
        set({ isLoading: value });
      },

      isInterrupt: false,
      setInterrupt: (value: boolean) => {
        set({ isInterrupt: value });
      },

      showNavAIPanel: false,
      setShowNavAIPanel: (value: boolean) => {
        set({
          showNavAIPanel: value,
        });
      },

      isShowNavigationContinueButton: false,
      setIsShowNavigationContinueButton: (value: boolean) => {
        set({
          isShowNavigationContinueButton: value,
        })
      },
      isShowNavigationRestartButton: false,
      setIsShowNavigationRestartButton: (value: boolean) => {
        set({
          isShowNavigationRestartButton: value,
        })
      },

      isDisplayUI: false,
      setIsDisplayUI: (value: boolean) => {
        set({ isDisplayUI: value });
      },

      isDisplayDragScreenSliderAnimation: false,
      setIsDisplayDragScreenSliderAnimation: (value: boolean) => {
        set({ isDisplayDragScreenSliderAnimation: value })
      },

      startVenueId: "",
      startMapId: "",
      startCoordinate: "",
      startPoi: "",
      setStartPosition: (
        venueId: string,
        mapId: string,
        coordinate: string,
        poi: string,
      ) => {
        set({
          startVenueId: venueId,
          startMapId: mapId,
          startCoordinate: coordinate,
          startPoi: poi,
        });
      },

      resetNavigationSessionData: () => {
        set({
          chatAnswer: "",
          userQuery: "",
          traceId: "",
          venueNotSupported: false,
          apiError: "",
          fullDistance: -1,
          remainingDuration: -1,
          navigationAngle: 0,
          navigationMessage: "",
          currentLevel: "",
          navFrom: "",
          navigationCheckpoint: [],
          navigationCheckpointDataArray: [],
          progress: -1,
          startVenueId: "",
          startMapId: "",
          startCoordinate: "",
          startPoi: "",
          chatHistory: [],
        });
      },
    }),
    {
      name: "nav-storage", // localStorage key
      partialize: (state) => ({
        navMode: state.navMode,
      }),
    },
  ),
);
